import React, { useMemo, useState } from 'react';
import { ChevronRightIcon, UsersIcon } from 'lucide-react';
import { ProCustomer, ProJob } from '../../types/pro';
import { formatDate } from '../../utils/pro';
import { PageHeader } from '../../components/pro/PageHeader';
import { SearchField } from '../../components/pro/SearchField';
import { EmptyState } from '../../components/pro/EmptyState';
interface ProCustomersProps {
  customers: ProCustomer[];
  jobs: ProJob[];
  onOpen: (customerId: string) => void;
}
type Filter = 'alle' | 'aktive' | 'uden-opgaver';
export function ProCustomers({
  customers,
  jobs,
  onOpen
}: ProCustomersProps) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('alle');
  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return customers.map(customer => {
      const own = jobs.filter(job => job.customerId === customer.id);
      const active = own.filter(job => !['faerdig', 'faktureret'].includes(job.status)).length;
      const lastActivity = own.map(job => job.start ?? '').filter(Boolean).sort().pop();
      return {
        customer,
        active,
        lastActivity: lastActivity || customer.createdDate
      };
    }).filter(({
      customer,
      active
    }) => {
      if (filter === 'aktive' && active === 0) return false;
      if (filter === 'uden-opgaver' && active > 0) return false;
      if (!term) return true;
      return [customer.name, customer.customerNo, customer.address, customer.city, customer.phone, customer.email].join(' ').toLowerCase().includes(term);
    }).sort((a, b) => a.customer.name.localeCompare(b.customer.name, 'da'));
  }, [customers, jobs, query, filter]);
  const filters: {
    id: Filter;
    label: string;
  }[] = [{
    id: 'alle',
    label: 'Alle'
  }, {
    id: 'aktive',
    label: 'Med aktive opgaver'
  }, {
    id: 'uden-opgaver',
    label: 'Uden aktive opgaver'
  }];
  return <div data-id="element-748" data-mp="8k5xjg9ykma">
      <PageHeader title="Kunder" subtitle={`${customers.length} kunder i kartoteket`} data-id="element-749" data-mp="8am6dltp9p1" />

      <div className="mb-4 flex flex-wrap items-center gap-3" data-id="element-750" data-mp="a4dpvnwbpge3">
        <SearchField value={query} onChange={setQuery} placeholder="Søg navn, adresse, telefon…" className="min-w-[220px] flex-1" data-id="element-751" data-mp="a5yl7fqgp3d0" />
        <div role="group" aria-label="Filtrer kunder" className="flex rounded-lg border border-stone-200 bg-white p-1" data-id="element-752" data-mp="j40qi7h8ai63">
          {filters.map(option => <button key={option.id} onClick={() => setFilter(option.id)} aria-pressed={filter === option.id} className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${filter === option.id ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-753" data-mp="stttwl77vqz9">
              {option.label}
            </button>)}
        </div>
      </div>

      {rows.length === 0 ? <EmptyState icon={UsersIcon} title="Ingen kunder matcher" description="Prøv en anden søgning eller et andet filter." data-id="element-754" data-mp="17hu1gz1y5y2k" /> : <>
          {/* Desktop table — density matters when scanning many customers */}
          <div className="hidden overflow-hidden rounded-xl border border-stone-200 bg-white md:block" data-id="element-755" data-mp="xqpmw0g6of89">
            <table className="w-full text-sm" data-id="element-756" data-mp="12ktzp0c4rhh9">
              <thead data-id="element-757" data-mp="os5d5oslpfw6">
                <tr className="border-b border-stone-200 bg-stone-50 text-left" data-id="element-758" data-mp="osnf921y2oc0">
                  {['Kunde', 'Adresse', 'Telefon', 'E-mail', 'Aktive opgaver', 'Seneste aktivitet'].map(heading => <th key={heading} scope="col" className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-759" data-mp="1m5nwb6phdd7b">
                      {heading}
                    </th>)}
                  <th scope="col" className="w-10" data-id="element-760" data-mp="18eaga2qs6xh1" />
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100" data-id="element-761" data-mp="18cg1z7a0m3lx">
                {rows.map(({
              customer,
              active,
              lastActivity
            }) => <tr key={customer.id} onClick={() => customer.id && onOpen(customer.id)} className="cursor-pointer transition-colors hover:bg-stone-50" data-id="element-762" data-mp="yftleuoq1ypp">
                    <td className="px-4 py-3" data-id="element-763" data-mp="pgzhej92a624">
                      <span className="block font-medium text-stone-900" data-id="element-764" data-mp="2os8x91e0wr1v">
                        {customer.name}
                      </span>
                      <span className="block text-xs text-stone-400" data-id="element-765" data-mp="2juqvpw2c5e3w">
                        {customer.customerNo}
                        {customer.contactPerson && ` · ${customer.contactPerson}`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-600" data-id="element-766" data-mp="312i7gayrrryp">
                      {customer.address}
                      <span className="block text-xs text-stone-400" data-id="element-767" data-mp="360td09i17gb4">
                        {customer.postalCode} {customer.city}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-stone-600" data-id="element-768" data-mp="2dcnvdvm9y7fi">
                      {customer.phone}
                    </td>
                    <td className="max-w-[200px] truncate px-4 py-3 text-stone-600" data-id="element-769" data-mp="2iaz4230u2bw5">
                      {customer.email}
                    </td>
                    <td className="px-4 py-3" data-id="element-770" data-mp="3tryusu7j8io2">
                      {active > 0 ? <span className="inline-flex rounded-full bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700" data-id="element-771" data-mp="1nd1uhyjc0qb">
                          {active}
                        </span> : <span className="text-stone-300" data-id="element-772" data-mp="1tpz8uom30d86">—</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-stone-500" data-id="element-773" data-mp="27nnva0vsrsfc">
                      {formatDate(lastActivity)}
                    </td>
                    <td className="px-2" data-id="element-774" data-mp="1f09v7twsygr0">
                      <ChevronRightIcon className="h-4 w-4 text-stone-300" data-id="element-775" data-mp="1f1beumrvwbkc" />
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Phone — rows become compact cards */}
          <ul className="space-y-2 md:hidden" data-id="element-776" data-mp="1orxp01gu09vn">
            {rows.map(({
          customer,
          active
        }) => <li key={customer.id} data-id="element-777" data-mp="w9sgit8t1lon">
                <button onClick={() => customer.id && onOpen(customer.id)} className="flex w-full items-center gap-3 rounded-xl border border-stone-200 bg-white p-4 text-left" data-id="element-778" data-mp="w90n9p2ok01j">
                  <span className="min-w-0 flex-1" data-id="element-779" data-mp="32wrgc8cvz9n2">
                    <span className="block truncate font-medium text-stone-900" data-id="element-780" data-mp="37pav38nb8atg">
                      {customer.name}
                    </span>
                    <span className="block truncate text-xs text-stone-500" data-id="element-781" data-mp="2tz7eoyb43bbx">
                      {customer.address}, {customer.city}
                    </span>
                    <span className="mt-1 block text-xs text-stone-400" data-id="element-782" data-mp="3mls5nxnodley">
                      {customer.phone}
                    </span>
                  </span>
                  {active > 0 && <span className="flex-shrink-0 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700" data-id="element-783" data-mp="3conetkivx1rt">
                      {active} aktive
                    </span>}
                  <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-stone-300" data-id="element-784" data-mp="217nrz4fb3f56" />
                </button>
              </li>)}
          </ul>
        </>}
    </div>;
}